import { useEffect, useRef, useCallback } from 'react';

const DEFAULT_IDLE_MS = 300_000; // 5 minutes
const DEFAULT_CLOSE_MS = 180_000; // 3 minutes
const CHECK_INTERVAL_MS = 30_000; // check every 30s

interface UseIdleTimeoutOptions {
  enabled: boolean;
  idleMs?: number;
  closeMs?: number;
  hasMessages: boolean;
  onIdle: () => void;
  onTimeout: () => void;
}

export function useIdleTimeout({
  enabled,
  idleMs = DEFAULT_IDLE_MS,
  closeMs = DEFAULT_CLOSE_MS,
  hasMessages,
  onIdle,
  onTimeout,
}: UseIdleTimeoutOptions) {
  const lastActivityRef = useRef(Date.now());
  const idleFiredRef = useRef(false);
  const timeoutFiredRef = useRef(false);

  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    idleFiredRef.current = false;
    timeoutFiredRef.current = false;
  }, []);

  // Expose resetActivity for external callers (e.g. on user send)
  useEffect(() => {
    if (!enabled || !hasMessages) return;

    const interval = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;

      if (!idleFiredRef.current && elapsed >= idleMs) {
        idleFiredRef.current = true;
        onIdle();
      }

      if (!timeoutFiredRef.current && elapsed >= idleMs + closeMs) {
        timeoutFiredRef.current = true;
        onTimeout();
      }
    }, CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [enabled, hasMessages, idleMs, closeMs, onIdle, onTimeout]);

  return { resetActivity };
}
